import { useEffect } from "react";

import MOBILE from "./mobileConfig";

/**
 * Drag with a mouse to pan, the way a finger pans on a phone.
 *
 * WHY THIS EXISTS. The breakpoint is width-only, so the mobile layout is what
 * you get by making a desktop window narrow — which is how it is developed and
 * reviewed. But a narrow desktop window has no finger in it: the wheel works,
 * and the scrollbar works, and dragging does nothing. This closes that gap so
 * the layout can be driven the way it will actually be used.
 *
 * MOUSE ONLY, and that guard is the whole reason this is safe to ship. Browsers
 * fire compatibility mouse events after touch, so without `pointerType` a real
 * phone would scroll natively AND again from here — every drag at double speed.
 * `pointerdown` with `pointerType === "mouse"` never fires from a finger.
 *
 * Three more things it must not break:
 *
 *  - CLICKS. A click is a drag of nought pixels. Nothing happens until the
 *    pointer has moved THRESHOLD, and once it has, the click that follows the
 *    release is swallowed — otherwise dragging off a card would also open it.
 *  - TEXT SELECTION AND TYPING. A drag that starts in an input, a textarea or
 *    anything editable belongs to that control, not to the page.
 *  - THE AXIS THAT ALREADY SCROLLS. The tab rail scrolls sideways; the page
 *    scrolls down. The axis is decided once, from the first movement, and the
 *    element panned is the nearest ancestor that can actually scroll on it —
 *    so dragging the rail moves the rail and dragging a card moves the page.
 */

/** Movement before a press becomes a drag. */
const THRESHOLD = 4;

/** Controls whose own drag behaviour must win. */
const INTERACTIVE = "input, textarea, select, option, [contenteditable], [draggable='true']";

const canScroll = (el, axis) => {
  if (!el || el.nodeType !== 1) return false;
  const style = window.getComputedStyle(el);
  const overflow = axis === "x" ? style.overflowX : style.overflowY;
  if (overflow !== "auto" && overflow !== "scroll") return false;
  return axis === "x"
    ? el.scrollWidth > el.clientWidth + 1
    : el.scrollHeight > el.clientHeight + 1;
};

/** Nearest ancestor that can be panned on `axis`, else the document. */
const scrollerFor = (target, axis) => {
  let node = target;
  while (node && node.nodeType === 1 && node !== document.body) {
    if (canScroll(node, axis)) return node;
    node = node.parentNode;
  }
  return axis === "y" ? document.scrollingElement || document.documentElement : null;
};

const useDragScroll = () => {
  useEffect(() => {
    if (!MOBILE.dragScroll || typeof window === "undefined") return undefined;

    let startX = 0;
    let startY = 0;
    let axis = null;
    let scroller = null;
    let live = false;
    let dragged = false;

    const onDown = (e) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      if (e.target.closest && e.target.closest(INTERACTIVE)) return;
      startX = e.clientX;
      startY = e.clientY;
      axis = null;
      scroller = null;
      live = true;
      dragged = false;
    };

    const onMove = (e) => {
      if (!live) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (!axis) {
        if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
        axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        scroller = scrollerFor(e.target, axis);
        if (!scroller) {
          live = false;
          return;
        }
        dragged = true;
        document.documentElement.classList.add("zym-dragging");
      }

      /* Content follows the pointer, so the page moves the way a finger would
         take it — drag down, the content comes down, scrollTop goes up. */
      if (axis === "x") scroller.scrollLeft -= e.movementX;
      else scroller.scrollTop -= e.movementY;

      // Stops the browser starting a text selection behind the drag.
      e.preventDefault();
    };

    const end = () => {
      live = false;
      axis = null;
      scroller = null;
      document.documentElement.classList.remove("zym-dragging");
    };

    /* Capture phase, and only after a real drag: the click that follows a
       release would otherwise land on whatever the drag started on. */
    const onClick = (e) => {
      if (!dragged) return;
      dragged = false;
      e.stopPropagation();
      e.preventDefault();
    };

    document.addEventListener("pointerdown", onDown);
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", end);
    document.addEventListener("pointercancel", end);
    /* Releasing outside the window fires no pointerup, which would leave the
       page stuck mid-drag — panning on the next mouse move with no button
       down. Ending on the way out is the cheap fix; setPointerCapture is the
       other one, and it needs an element to capture on. */
    document.addEventListener("pointerleave", end);
    window.addEventListener("blur", end);
    document.addEventListener("click", onClick, true);

    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", end);
      document.removeEventListener("pointercancel", end);
      document.removeEventListener("pointerleave", end);
      window.removeEventListener("blur", end);
      document.removeEventListener("click", onClick, true);
      document.documentElement.classList.remove("zym-dragging");
    };
  }, []);
};

export default useDragScroll;
